import type {
  FeefoProductReview,
  FeefoProductReviewsResponse,
  FeefoReview,
  FeefoReviewMediaItem,
} from 'src/services/feefo-api/types/FeefoProductReviewsResponse';
import type {
  ProductReview,
  ProductReviewReply,
  ProductReviewsPage,
  ReviewMediaItem,
} from '../types';

/**
 * Compared case-insensitively: Feefo's docs give `merchant_comment`, the live API sends
 * `MERCHANT_COMMENT`.
 */
const MERCHANT_COMMENT_TYPE = 'merchant_comment';

const PHOTO = 'photo';
const VIDEO = 'video';

/** Feefo sends both "" and undefined for "no value". */
const cleaned = (value: string | undefined): string | undefined => value?.trim() || undefined;

/**
 * Anything not a video is a photo: Feefo documents only the two, and a broken image beats
 * dropping the customer's contribution.
 */
const mediaType = (type: string | undefined): ReviewMediaItem['type'] =>
  type?.toLowerCase() === VIDEO ? VIDEO : PHOTO;

/**
 * Feefo keeps moderated-out items in the array with `removed` set rather than dropping
 * them, so they are dropped here.
 */
const adaptMedia = (media: FeefoReviewMediaItem[] | undefined): ReviewMediaItem[] =>
  (media ?? []).reduce<ReviewMediaItem[]>((items, item) => {
    const url = cleaned(item.url);
    const id = cleaned(item.id);
    const caption = cleaned(item.caption);
    const thumbnailUrl = cleaned(item.thumbnail);

    if (url && id && item.removed !== true) {
      items.push({
        id,
        type: mediaType(item.type),
        url,
        ...(caption && { caption }),
        ...(thumbnailUrl && { thumbnailUrl }),
      });
    }

    return items;
  }, []);

/**
 * The first `MERCHANT_COMMENT`, not the last: threads run oldest to newest and the opening
 * reply is the one that answers the review.
 */
const adaptReply = (review: FeefoProductReview): ProductReviewReply | undefined => {
  const entry = review.thread?.find(({ type }) => type?.toLowerCase() === MERCHANT_COMMENT_TYPE);
  const text = cleaned(entry?.comment);

  if (!entry || !text) {
    return undefined;
  }

  return {
    text,
    ...(cleaned(entry.author) && { author: cleaned(entry.author) }),
    ...(cleaned(entry.created_at) && { date: cleaned(entry.created_at) }),
  };
};

/** Undefined when neither half is present, which on live data is most reviews. */
const adaptReviewer = (review: FeefoReview): ProductReview['reviewer'] => {
  const name = cleaned(review.customer?.display_name);
  const location = cleaned(review.customer?.display_location);

  if (!name && !location) {
    return undefined;
  }

  return { ...(name && { name }), ...(location && { location }) };
};

/**
 * Null without an id, date and rating: dropping beats a review dated the epoch or rated
 * zero. Text and media are optional, since Feefo sends rating-only reviews.
 */
const adaptReview = (review: FeefoReview): ProductReview | null => {
  // A product-SKU query returns one product per review, so the first is the only.
  const productReview = review.products?.[0];
  const id = cleaned(productReview?.id);
  const date = cleaned(productReview?.created_at);
  const rating = productReview?.rating?.rating;

  if (!productReview || !id || !date || !rating) {
    return null;
  }

  const reply = adaptReply(productReview);
  const reviewer = adaptReviewer(review);
  const text = cleaned(productReview.review);

  return {
    date,
    id,
    media: adaptMedia(productReview.media),
    rating,
    ...(reply && { reply }),
    ...(reviewer && { reviewer }),
    ...(text && { text }),
  };
};

/**
 * Pagination comes from `summary.meta`, which narrows with the `rating` filter. A missing
 * meta field falls back to this page as page 1 of 1, so no non-existent page 2 is offered.
 */
export const adaptFeefoReviewsToStore = (
  response: FeefoProductReviewsResponse,
): ProductReviewsPage => {
  const reviews = (response.reviews ?? [])
    .map(adaptReview)
    .filter((review): review is ProductReview => review !== null);

  const meta = response.summary?.meta;
  const pageSize = meta?.page_size || reviews.length;

  return {
    meta: {
      currentPage: meta?.current_page || 1,
      pageSize,
      totalCount: meta?.count ?? reviews.length,
      totalPages: meta?.pages || 1,
    },
    reviews,
  };
};
